<template>
  <div class="p-4 w-full mx-auto">
    <div class="mb-4 flex items-center justify-between">
      <div>
        <div class="flex items-center">
          <MessageSquareText class="w-5 h-5 mr-1 text-gray-700" />
          <h1 class="text-xl font-normal text-gray-800">
            Internal Comments
          </h1>
          <span class="ml-2 text-sm text-gray-500">[{{ totalCount }}]</span>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <!-- Search Bar -->
        <div class="relative w-[300px]">
          <Search
            class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
          />
          <input
            type="text"
            placeholder="Search comments..."
            class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            v-model="searchQuery"
            @input="handleSearch"
          />
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="flex justify-center items-center py-8 min-h-[calc(100vh-8rem)]"
    >
      <OuttalentLoader full-title />
    </div>

    <div
      v-else-if="error"
      class="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
    >
      <p class="text-red-600">{{ error }}</p>
    </div>

    <div v-else>
      <!-- Comments Table -->
      <div class="bg-white">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200 w-32"
                >
                  Created
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  For User
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Comment
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="comment in filteredComments"
                :key="comment.id"
                class="hover:bg-gray-50 cursor-pointer"
                @click="!isBlurred && goToUserProfile(comment.user_details.username)"
              >
                <!-- Created By and When Column -->
                <td v-if="comment.created_by.username == 'admin'" class="py-3 px-4 border border-gray-100 w-36 align-top">
                  <div class="text-xs text-gray-500">Legacy Airtable Comment</div>
                </td>
                <td v-else class="py-3 px-4 border border-gray-100 w-36 align-top">
                  <div class="flex items-center gap-1">
                    <div v-if="comment.created_by.avatar_image" class="w-4 h-4 rounded-full overflow-hidden">
                      <img
                        :src="comment.created_by.avatar_image"
                        :alt="`${comment.created_by.first_name} ${comment.created_by.last_name}`"
                        class="w-full h-full object-cover"
                        :class="{ 'blur-md select-none': isBlurred }"
                      />
                    </div>
                    <div
                      v-else
                      class="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
                    >
                      <div class="w-full h-full flex items-center justify-center" :class="{ 'blur-md select-none': isBlurred }">
                        <User class="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <span class="text-xs text-gray-900" :class="{ 'blur-md select-none': isBlurred }"
                      >{{ comment.created_by.first_name }} {{ comment.created_by.last_name }}</span
                    >
                  </div>
                  <div class="text-xs text-gray-500">{{ formatDateUtil(comment.created_at) }}</div>
                </td>
                <!-- User Column -->
                <td class="py-2 px-4 border border-gray-100 w-[20rem] align-top">
                  <div class="flex items-center gap-3">
                    <component
                      :is="isBlurred ? 'div' : 'router-link'"
                      :to="isBlurred ? undefined : `/profile/${comment.user_details.username}`"
                      class="hover:scale-110 transition-all duration-300"
                    >
                      <div v-if="comment.user_details?.avatar_image" class="w-8 h-8 rounded-full overflow-hidden">
                        <img
                          :src="comment.user_details.avatar_image"
                          :alt="`${comment.user_details.first_name} ${comment.user_details.last_name}`"
                          class="w-full h-full object-cover"
                          :class="{ 'blur-md select-none': isBlurred }"
                        />
                      </div>
                      <div
                        class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
                        v-else
                      >
                        <div class="w-full h-full flex items-center justify-center" :class="{ 'blur-md select-none': isBlurred }">
                          <User class="w-4 h-4 text-gray-600" />
                        </div>
                      </div>
                    </component>
                    <div>
                      <div class="text-sm font-medium text-gray-900 flex items-center gap-2" :class="{ 'blur-md select-none': isBlurred }">
                        {{ comment.user_details.first_name }} {{ comment.user_details.last_name }}
                        <a
                          v-if="!isBlurred && comment.user_details.linkedin_profile_url"
                          @click.stop
                          :href="comment.user_details.linkedin_profile_url"
                          target="_blank"
                          class="text-xs text-gray-400 hover:scale-110 transition-all duration-300"
                        >
                          <img src="/linkedin_logo.svg" alt="LinkedIn" class="w-4 h-4" />
                        </a>
                        <a
                          v-if="!isBlurred && comment.user_details.telegram_username"
                          @click.stop
                          :href="comment.user_details.telegram_username"
                          target="_blank"
                          class="text-xs text-gray-400 hover:scale-110 transition-all duration-300"
                        >
                          <img src="/telegram_logo.svg" alt="Telegram" class="w-4 h-4" />
                        </a>
                      </div>
                      <div class="text-xs text-gray-400">
                        {{ formatBatchName(comment.fellow_details?.batch || '') }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Comment Column -->
                <td class="py-3 px-4 border border-gray-100 align-top">
                  <div class="flex text-xs gap-1">
                    <MarkdownRenderer :content="comment.comment" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="filteredComments.length === 0 && !loading" class="text-center py-12">
          <div class="text-gray-500">No comments found for this expedition</div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6">
        <PaginationComponent
          :current-page="currentPage"
          :total-pages="totalPages"
          :use-router="true"
          @update:page="handlePageChange"
          class="mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import { MessageSquareText, User, Search } from 'lucide-vue-next'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useDebounce } from '@/composables/useDebounce'
import { ref, computed, onMounted, watch } from 'vue'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import type { InternalComment } from '@/types/userProfile'
import { formatBatchName } from '@/utils/formatBatchName'
import MarkdownRenderer from '@/components/ui/markdown/MarkdownRenderer.vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const router = useRouter()
const route = useRoute()
const { isBlurred } = usePrivacyMode()
const { debounce } = useDebounce(300)

const pageSize = 20

// Local state for expedition-specific comments
const comments = ref<InternalComment[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const totalCount = ref(0)
const currentPage = ref(1)
const searchQuery = ref('')

const expeditionId = computed(() => route.params.id as string)

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize))

const filteredComments = computed(() => {
  if (!searchQuery.value.trim()) {
    return comments.value
  }
  
  const query = searchQuery.value.toLowerCase()
  return comments.value.filter(c => 
    c.user_details.first_name.toLowerCase().includes(query) ||
    c.user_details.last_name.toLowerCase().includes(query) ||
    c.user_details.email.toLowerCase().includes(query) ||
    c.comment?.toLowerCase().includes(query)
  )
})

const fetchComments = async (page = 1) => {
  loading.value = true
  error.value = null
  
  try {
    const params: Record<string, any> = { 
      page, 
      page_size: pageSize,
      expedition: expeditionId.value
    }

    const response = await api.get(API_ROUTES.GET_INTERNAL_COMMENTS, { params })
    comments.value = response.data.results
    totalCount.value = response.data.count
    currentPage.value = page
  } catch (err) {
    console.error('Error fetching expedition comments:', err)
    error.value = 'Failed to fetch comments'
  } finally {
    loading.value = false
  }
}

const handleSearch = debounce(() => {
  // Search is handled locally via computed property
})

const handlePageChange = (page: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchComments(page)
}

const goToUserProfile = (username: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/profile/${username}`)
}

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchComments()
})

// Watch for expedition ID changes
watch(
  () => route.params.id,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchComments()
  }
)
</script>

